import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_TABLE_SETTINGS,
  normalizeTableSettings,
  type TableSettings,
} from '@jisy/shared';
import {
  accountQuery,
  useTableSettingsMutation,
  type AccountResponse,
} from '../api/queries.js';

/**
 * Личные настройки таблицы задач.
 *
 * Хранятся на сервере, но интерфейс не ждёт ответа: изменение сразу ложится в кэш запроса
 * `account`, а запись уходит с задержкой. Иначе протягивание колонки — это десятки запросов
 * подряд, каждый из которых догоняет уже неактуальное состояние.
 */
const SAVE_DELAY = 500;

export interface TableSettingsControl {
  settings: TableSettings;
  /** Изменение задаётся функцией от текущих настроек: подряд идущие правки не теряют друг друга. */
  update: (change: (current: TableSettings) => TableSettings) => void;
}

export function useTableSettings(): TableSettingsControl {
  const queryClient = useQueryClient();
  const account = useQuery(accountQuery);
  const save = useTableSettingsMutation();

  const timer = useRef<number | null>(null);
  const pending = useRef<TableSettings | null>(null);
  // Мутация пересоздаётся на каждый рендер, а отправка живёт в таймере — держим её в ссылке.
  const saveRef = useRef(save);
  saveRef.current = save;

  const flush = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const next = pending.current;
    pending.current = null;
    if (next) saveRef.current.mutate(next);
  }, []);

  const update = useCallback(
    (change: (current: TableSettings) => TableSettings) => {
      const current = queryClient.getQueryData<AccountResponse>(accountQuery.queryKey);
      if (!current) return;

      const next = normalizeTableSettings(change(current.tableSettings));
      queryClient.setQueryData<AccountResponse>(accountQuery.queryKey, {
        ...current,
        tableSettings: next,
      });

      pending.current = next;
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        const settings = pending.current;
        pending.current = null;
        if (settings) saveRef.current.mutate(settings);
      }, SAVE_DELAY);
    },
    [queryClient],
  );

  // Уход с экрана не должен съедать последнее изменение — дописываем его немедленно.
  useEffect(() => flush, [flush]);

  return { settings: account.data?.tableSettings ?? DEFAULT_TABLE_SETTINGS, update };
}
